import React, { useContext, useState, useCallback } from 'react';
import { ShopperContext } from '../context/shopper';
import * as Constants from '../constants';
import { ErrorList } from './ErrorList';
import { useForm } from "../hooks/useForm";
import { useUtils } from "../hooks/useUtils";
import { usePhrasing, useLookups } from "../hooks/useSkuUtils";
import { useStyles } from "../hooks/useStyles";
import { currencyDecimals } from '../utils';
import HeadingBuilder from './HeadingBuilder';
import SkuCategoryPicker from './SkuCategoryPicker';
import { BackButton } from './BackButton';
import { NumericFormat } from 'react-number-format';
import Table from 'react-bootstrap/Table';
import { useTranslation } from "react-i18next";
import styles from '../css/trTdFirstChild.module.css';
import MyScannerA from './MyScannerA';

const SkuForm = ({circle_id, skuType, resetSkuTypeFormFlags, chosenUuid}) => {
  const { signedIn, shopper, addSku, circleShoppers, circles,
          barcodeScanningIsActive,
          foundSkus, stagedSku, unsetFoundSkus } = useContext(ShopperContext);
  const { errorsDoExist, dismissErrorsList, setErrorsDoExistToFalse } = useContext(ShopperContext);
  const { setJustGotClearToFalse } = useContext(ShopperContext);
  const { tableShow, tableData, altBorderTopColor } = useStyles();
  const [ showScanner, setShowScanner ] = useState(false);


  // An 'off-' uuid is a pseudo-sku staged from an OFF lookup, held
  // apart from foundSkus.
  const search_sku_attrs =
    String(chosenUuid).startsWith('off-')
      ? stagedSku
      : foundSkus.find(fs =>  fs.uuuid == chosenUuid);
  console.log('SkuForm.js search_sku_attrs: ', search_sku_attrs)

  console.log('SkuForm.js skuType: ', skuType)
  const isMultipack= skuType == "MultipackItem" ? true : false;
  const isSingular = skuType == "SingularItem" ? true : false;
  const isWeighty  = skuType == "WeightyItem" ? true : false;

  console.log('SkuForm.js circle_id: ', circle_id)
  const { t } = useTranslation();
  const { is_shopper_a_circle_admin } = useUtils();
  const { tippySkuIdentifierIsWeightyString,
          typicalWeightLabel,
          typicalWeightTippyText,
          priceLabel,
          priceTippyText,
          getSkuQtyTippyContent,
          getSkuUnitTippyContent,
          getTippyShareSkuPerCircle,
          getSkuCatTippyInfo,
          tippySkuDetailDquoteNote,
      } = usePhrasing();

  const if_shopper_is_circle_admin = is_shopper_a_circle_admin({
    cShoppers: circleShoppers, circle_id: circle_id, shopper_id: shopper.id
  });

  const circleObj = circles.find(c => c.id == circle_id);
  const circleShareSkuBasic = circleObj.share_sku_basic;

  const { lookup_category_eg } = useLookups();

  const initialState = {
    circle_id: circle_id,
    shopper_id: '',
    currency: '$',
    //price_date: Date.today,
    price_date: '',
    discount_amount: '0',
    discount_start_date: '',
    discount_end_date: '',
    store_label: '',
    identifier: '',
    brand: '',
    label: '',
    detail: '',
    category: '',
    unit_count: (isSingular || isWeighty) ? 1 : '',
    typical_weight: '',
    price: '',
    tax_type: 2,  // 2 => 'Food'
    shareable: 0,
    is_hidden: 0,
    quantity: isWeighty ? 1 : '',
    units: '',
    unit_price: '',
    type: skuType,
  };

  // A typeless search_sku is a pseudo-sku staged from an OFF barcode
  // lookup: its fields prefill whichever sku type the shopper picks.
  if (search_sku_attrs  &&
      (!search_sku_attrs.type  ||  skuType == search_sku_attrs.type)) {
    for (const property in search_sku_attrs) {
      // don't add these properties to 'initialState'
      if ( [
             'shared_circle_identifier',
             'shared_circle_postal_code',
             'shared_circle_xip_code',
             'uuuid'
           ].indexOf(property) >= 0
         ) {continue;}
      
      // properties whose values came from Price start with 'shared_'.
      // To load these values into initialState, strip 'shared_' from property.
      const initStateProperty =
        property.indexOf('shared_') === 0  ? property.substring(7) : property;

      initialState[initStateProperty] = search_sku_attrs[property];
    }
  }

  const { formData, handleChange, handleChangeWithWhitespace,
          appendFieldValue,
          // reset, editFormValues,
            } = useForm(initialState);

  const handleOpenScanner = useCallback(() => {
    setShowScanner(true);
  }, []);
  
  const handleCloseScanner = useCallback(() => {
    setShowScanner(false);
  }, []);

  const wants12digitEAN =  circleObj.symbology === 'upca' ? true : false;

  const tippyCategoryInfo =
    t('global.eg') + ' ' + lookup_category_eg({category: formData.category});
    // e.g. cookies, crackers, popcorn, pretzels
    
 if (signedIn) {

  const handleSubmit = (e) => {
    e.preventDefault();

    // clear out any error messages, success statuses
    dismissErrorsList();
    setErrorsDoExistToFalse();
    setJustGotClearToFalse();

    console.log('formData for addSku: ', formData);
    addSku(formData);

    // If there is an error we want to preserve formData and show the message.

    // Clear out foundSkus (and chosenUuid) to reset attrs for this type
    unsetFoundSkus();
  }
  const tippySkuIdentifier =
    t('sku.identifier.tippyContent', {
      postscript:
    barcodeScanningIsActive
      ?
          t('sku.identifier.barScanTippyContentA')
        + t('sku.identifier.barScanTippyContentB', {
            barcodeAction: t('sku.identifier.appendsTo'),
          })
        + t('sku.identifier.tippyContent2')
        + tippySkuIdentifierIsWeightyString({isWeighty})
      : ''
    })

  const skuLabelPlaceholder =
    isWeighty
      ? t('sku.label.placeholderByWeight')
      : isSingular
        ? t('sku.label.placeholderSingular')
        : t('sku.label.placeholder')

  const skuQuantityPlaceholder =
    isSingular
      ? t('sku.quantity.placeholderSingular')
      : t('sku.quantity.placeholder')

  const skuUnitsPlaceholder =
    isWeighty
      ? t('sku.units.placeholderByWeight')
      : isSingular
        ? t('sku.units.placeholderSingular')
        : t('sku.units.placeholder')

  const  skuQtyTippyContent =  getSkuQtyTippyContent({skuType});
  const skuUnitTippyContent = getSkuUnitTippyContent({skuType});

  return (
      <article>
        <form onSubmit={handleSubmit}>
<Table responsive striped bordered hover size="sm" style={tableShow}>
  <thead >
  </thead>
  <tbody className={styles.trTdFC} style={tableData} >
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.store_label.label')}
          // Store
          labelHtmlFor = "store_label"
          tippyAlertText = {t('sku.store_label.tippyContent')}
          tippyText      = {t('sku.store_label.tippyContent')}
          tippyInfoText  = {t('sku.store_label.tippyContent2')}
        />
        {/* This item will be purchased at which store(s)? You might include... */}
      </td>
      <td>
        <input
          key="10"
          type="text"
          id="store_label"
          name="store_label"
          placeholder={t('sku.store_label.placeholder', 'Topaz Mart')}
          onChange={handleChange}
          value={formData.store_label}
          required
        />
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('sku.identifier.label')}
          // Lookup ID
          buttonAction   = {
                             barcodeScanningIsActive
                               ? handleOpenScanner
                               : null
                           }
          goButtonText   = {
                             barcodeScanningIsActive
                               ? Constants.GET_ICON_BARCODE
                               : ''
                           }
          // buttonAction   = {handleOpenScanner}
          // goButtonText   = {Constants.GET_ICON_BARCODE}
          labelHtmlFor   = "identifier_skf"
          tippyAlertText = {tippySkuIdentifier}
          // A value that identifies the item such as a store code, barcode,
          // or a term you make up. Include only uppercase letters, numbers,
          // periods, underscores ('_'), and nonconsecutive spaces.
          tippyText      = {Constants.DOT}
          tippyInfoText  = {t('sku.identifier.tippyContentInfo')}
        />
      </td>
      <td>
        {showScanner
          ? <
            MyScannerA
              inputFieldName     = 'identifier'
              handleFieldValue   = {appendFieldValue}  // appends bcode to input
              // handleFieldValue   = {setFieldValue}  // replaces input w bcode
              handleCloseScanner = {handleCloseScanner}
              wants12digitEAN    = {wants12digitEAN}
            />
          :
        <input
          key="20"
          type="text"
          style={{ textTransform: "uppercase" }}
          id="identifier_skf"
          name="identifier"
          placeholder={t('sku.identifier.placeholder')}
          onChange={handleChangeWithWhitespace}
          value={formData.identifier || ""}
          required
        />
        }
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.brand.label')}
          // Brand
          labelHtmlFor = "brand"
          tippyAlertText = {t('sku.brand.tippyContent')}
          tippyText      = {t('sku.brand.tippyContent')}
          // e.g. Green Giant, General Mills, Heinz.
        />
      </td>
      <td>
        <input
          key="30"
          type="text"
          id="brand"
          name="brand"
          placeholder={t('sku.brand.placeholder')}
          onChange={handleChange}
          value={formData.brand}
          required
        />
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.label.label')}
          // Name/Label
          labelHtmlFor = "label"
          tippyAlertText = {t('sku.label.tippyContent')}
          tippyText      = {t('sku.label.tippyContent')}
          // Short, quick name of the item.
        />
      </td>
      <td>
        <input
          key="40"
          type="text"
          id="label"
          name="label"
          placeholder={skuLabelPlaceholder}
          onChange={handleChange}
          value={formData.label}
          required
        />
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.detail.label')}
          // Details
          labelHtmlFor = "detail"
          tippyAlertText = {t('sku.detail.tippyContent')}
          tippyText      = {Constants.DOT}
          // Any details you would like to add for the item.
          tippyInfoText  = {tippySkuDetailDquoteNote()}
          // Tech note: CSV files will convert any double-quote characters ...
        />
      </td>
      <td>
        <textarea
          key="50"
          id="detail"
          name="detail"
          placeholder={t('sku.detail.placeholder')}
          onChange={handleChange}
          value={formData.detail}
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.category.label')}
          // Category
          labelHtmlFor = "category"
          tippyAlertText = {t('sku.category.tippyContent')}
          tippyText      = {t('sku.category.tippyContent')}
          // What category is the item? (Helpful for sorting.)
          tippyInfoText  = {getSkuCatTippyInfo()}
        />
      </td>
      <td>
        <SkuCategoryPicker
          catKey       = "55"
          catId        = "category"
          formData     = {formData}
          handleChange = {handleChange}
        />
        &nbsp;
        <HeadingBuilder
          headingText   = '*'
          // '*' - the 'required' indicator for the field
          tippyText     = {tippyCategoryInfo}
          tippyInfoText = {tippyCategoryInfo}
        />
      </td>
    </tr>
    {
isMultipack
  ? <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.unit_count.label')}
          // Num Items in Sku
          labelHtmlFor = "unit_count"
          tippyAlertText = {t('sku.unit_count.tippyContent')}
          tippyText = {t('sku.unit_count.tippyContent')}
          tippyInfoText = {t('sku.unit_count.tippyContent2')}
        />
        {/* "tippyContent": "The item contains individual units. How many are there?" */}
      </td>
      <td>
        <NumericFormat displayType="input"
          key="60"
          type="text"
          id="unit_count"
          name="unit_count"
          onChange={handleChange}
          placeholder={t('sku.unit_count.placeholder')}
          value={formData.unit_count}
          decimalScale={2}
          hidden={isMultipack ? false : true}
          required
        />
        &nbsp;*
      </td>
    </tr>
      : null
    }
    {
isWeighty
  ? <tr>
      <td>
        {/* TODO: would be nice to put this in bold if we arrived via Link, i.e. by someone
        clicking on the "EST 12.5 lb" link in the ShoppingList. The bolding would make
        it easier to see the key field to edit. Signal by appending option to link? */}
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {typicalWeightLabel()}
          // Typical weight (Only a number, no units)
          labelHtmlFor = "typical_weight"
          tippyAlertText = {typicalWeightTippyText()}
          tippyText      = {typicalWeightTippyText()}
        />
        {/* If the item is sold by weight (e.g. Chicken Thighs are sold by the pound), how much does a typical package weigh? If a package of Chicken Thighs is typically 10 pounds, enter '10' here. */}
      </td>
      <td>
        <NumericFormat displayType="input"
          key="70"
          type="text"
          id="typical_weight"
          name="typical_weight"
          onChange={handleChange}
          value={formData.typical_weight}
          placeholder={t('sku.typical_weight.placeholder')}
          decimalScale={2}
          hidden={isWeighty ? false : true}
          required
        />
        &nbsp;*
      </td>
    </tr>
  : <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.quantity.label')}
          // Total Weight/Quantity
          labelHtmlFor = "quantity"
          tippyAlertText = {skuQtyTippyContent}
          tippyText      = {Constants.DOT}
          tippyInfoText  = {t('sku.quantity.dontSayUnits')}
        />
        {/* What is the total weight/quantity of the product? For example, if the package contains six 10-oz cans of something, the value here would be 60. ** Enter '1' if the item is priced by weight (meat, fish, etc.) */}
      </td>
      <td>
        <NumericFormat displayType="input"
          key="80"
          type="text"
          id="quantity"
          name="quantity"
          onChange={handleChange}
          placeholder={skuQuantityPlaceholder}
          value={formData.quantity}
          decimalScale={2}
          hidden={isWeighty ? true : false}
          required={isSingular ? true : false}
        />
        {
          isSingular ? <>&nbsp;*</> : null
        }
      </td>
    </tr>
    }
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.units.label')}
          // Units, e.g. oz, g, lb, ft2
          labelHtmlFor = "units"
          tippyAlertText = {skuUnitTippyContent}
          tippyText      = {Constants.DOT}
        />
        {/* What is the unit of measure for the item's quantity/total weight? Is it measured in ounces (oz), pounds (lb), square feet (ft2)? Maybe you are buying a pack of 40 AA batteries, so you would put 'AA battery'. */}
      </td>
      <td>
        <input
          key="90"
          type="text"
          id="units"
          name="units"
          placeholder={skuUnitsPlaceholder}
          onChange={handleChange}
          value={formData.units}
          required={(isWeighty || isSingular)  ? true : false}
        />
        {
          (isWeighty || isSingular)  && <span>&nbsp;*</span>
        }
      </td>
    </tr>
    {
  // Hardcoding with 'false' hides row for now. Maybe Circle should set this; here could be override, like how trip's tax rates can override circle's tax settings.
  false &&
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.currency.label')}
          // Currency Symbol
          labelHtmlFor = "currency_skf"
          // tippyAlertText = {t('sku.currency.tippyContent')}
          // tippyText      = {t('sku.currency.tippyContent')}
        />
      </td>
      <td>
        <input
          key="100"
          type="text"
          id="currency_skf"
          name="currency"
          onChange={handleChange}
          value={formData.currency}
          hidden={true}
        />
      </td>
    </tr>
    }
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {priceLabel({
              isWeighty,
              currency_symbol: circleObj.currency_symbol 
            })}
            // Price (decimal) $
          labelHtmlFor = "price_skf"
          tippyAlertText = {priceTippyText({
              isWeighty,
              currency_symbol: circleObj.currency_symbol 
            })}
          tippyText      = {Constants.DOT}
        />
        {/* A number showing the price of the item. Don't include a currency symbol such as '$'. */}
      </td>
      <td
        style={{
          background:  formData.price.length === 0 ? "lightblue" : "none"
        }} 
      >
        <NumericFormat displayType="input"
          key="110"
          type="text"
          id="price_skf"
          name="price"
          onChange={handleChange}
          value={formData.price}
          decimalScale={
            currencyDecimals({currency_subunit: circleObj.currency_subunit})
          }
          required
        />
        &nbsp;*
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.price_date.label')}
          // Price AsOf Date
          labelHtmlFor = "price_date_skf"
          tippyAlertText = {t('sku.price_date.tippyContent')}
          tippyText      = {t('sku.price_date.tippyContent')}
        />
        {/* When did/will the value in Price become the price? ShopWeAll will use the most recent 'Price AsOf Date' before a trip to price an item. */}
      </td>
      <td>
        <input
          key="115"
          type="date"
          id="price_date_skf"
          name="price_date"
          onChange={handleChange}
          value={formData.price_date}
          required
        />
        &nbsp;*
      </td>
    </tr>
    <tr style={altBorderTopColor}>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.discount_amount.label')}
          // Discount Amount
          labelHtmlFor = "discount_amount_skf"
          tippyAlertText = {t('sku.discount_amount.tippyContent')}
          tippyText      = {t('sku.discount_amount.tippyContent')}
        />
        {/* Value of any discount. This will be subtracted from the item's price to show the purchase price. */}
      </td>
      <td>
        <NumericFormat displayType="input"
          key="120"
          type="text"
          id="discount_amount_skf"
          name="discount_amount"
          onChange={handleChange}
          value={formData.discount_amount}
          decimalScale={
            currencyDecimals({currency_subunit: circleObj.currency_subunit})
          }
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.discount_start_date.label')}
          // Discount Start Date
          labelHtmlFor = "discount_start_date_skf"
          tippyAlertText = {t('sku.discount_start_date.tippyContent')}
          tippyText      = {t('sku.discount_start_date.tippyContent')}
        />
        {/* When will the discount begin? We start by assuming any discount is effective immediately, but you can change this. */}
      </td>
      <td>
        <input
          key="140"
          type="date"
          id="discount_start_date_skf"
          name="discount_start_date"
          onChange={handleChange}
          value={formData.discount_start_date}
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.discount_end_date.label')}
          // Discount End Date
          labelHtmlFor = "discount_end_date_skf"
          tippyAlertText = {t('sku.discount_end_date.tippyContent')}
          tippyText      = {t('sku.discount_end_date.tippyContent')}
        />
        {/* When will the discount end? */}
      </td>
      <td>
        <input
          key="150"
          type="date"
          id="discount_end_date_skf"
          name="discount_end_date"
          onChange={handleChange}
          value={formData.discount_end_date}
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.tax_type.label')}
          // Tax Status
          labelHtmlFor = "tax_type"
          tippyAlertText = {t('sku.tax_type.tippyContent')}
          tippyText      = {t('sku.tax_type.tippyContent')}
        />
        {/* What type of Sales Tax Rate will apply for this item? */}
      </td>
      <td>
        <select
          key="155"
          type="text"
          id="tax_type"
          name="tax_type"
          onChange={handleChange}
          value={formData.tax_type}
          required
        >
          {/* <option value="">Choose one:</option> */}
          <option value="">{t('sku.tax_type.optionValue')}</option>
          {/* <option selected value="0">No Tax</option> // wish this worked... */}
          {/* <option value="0">No Tax</option> */}
          <option value="0">{t('sku.tax_type.optionValue0')}</option>
          {/* <option value="1">Regular</option> */}
          <option value="1">{t('sku.tax_type.optionValue1')}</option>
          {/* <option value="2">Food</option> */}
          <option value="2">{t('sku.tax_type.optionValue2')}</option>
        </select>
        &nbsp;*
      </td>
    </tr>
    {
Constants.SHOW_SHAREABLE
  ? <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.shareable.label')}
          // Share details
          labelHtmlFor = "shareable"
          tippyAlertText = {t('sku.shareable.tippyContent')}
          tippyText      = {Constants.DOT}
          // Yes/No: Would you like to share this item's information with other
          // Shopping Circles?
          tippyInfoText  = {getTippyShareSkuPerCircle({circleShareSkuBasic})}
        />
      </td>
      <td>
        <select
          key="160"
          type="text"
          id="shareable"
          name="shareable"
          onChange={handleChange}
          value={formData.shareable}
        >
          {/* <option value="0">No</option> */}
          <option value="0">{t('sku.shareable.optionValue0')}</option>
          {/* <option value="1">Yes</option> */}
          <option value="1">{t('sku.shareable.optionValue1')}</option>
        </select>
      </td>
    </tr>
  : null 
    }
    {
if_shopper_is_circle_admin
  ? <tr>
      <td>
        <HeadingBuilder
          containerType='container-input-label'
          headingText = {t('sku.is_hidden.label')}
          // Hidden
          labelHtmlFor = "is_hidden"
          tippyAlertText = {t('sku.is_hidden.tippyContent')}
          tippyText      = {t('sku.is_hidden.tippyContent')}
        />
        {/* Do you want to reduce visibility of this Sku? e.g. not show it as one of the Circle's skus when it's time to plan a trip? etc. */}
      </td>
      <td>
        <select
          key="165"
          type="text"
          id="is_hidden"
          name="is_hidden"
          onChange={handleChange}
          value={formData.is_hidden}
        >
          {/* <option value="0">No</option> */}
          <option value="0">{t('sku.is_hidden.optionValue0')}</option>
          {/* <option value="1">Yes</option> */}
          <option value="1">{t('sku.is_hidden.optionValue1')}</option>
        </select>
      </td>
    </tr>
  : null 
    }
  </tbody>
</Table>
        {/* TODO: ?Will translating Submit be handled automatically by html?
            https://www.w3.org/International/wiki/Locale-based_forms */}
         <div className="oneline-container">
          <input
            key="170"
            type='submit'
            value='Submit'
            disabled={
              formData.price.trim().length === 0
                ||
              formData.price_date.trim().length === 0
            }
          />
          <HeadingBuilder
            tippyInfoText = {
                t('sku.submit.priceWarning10')
              + t('sku.price_date.label')
              + t('sku.submit.priceWarning20')
            }
            // Note: "Price" and "Price 'AsOf' Date" are required fields.
          />
         </div>
        </form>
        { errorsDoExist && <ErrorList /> }
        <BackButton whatToDo={resetSkuTypeFormFlags}/>
      </article>
  )
 } else {
    return (
      <HeadingBuilder
        Size = 'h3'
        headingText = {t('global.notSignedInMessage')}
        // Not Authorized - Please Sign in or Sign up
      />
    )
 }
}

export default SkuForm;